import React, {useEffect, useMemo, useState} from "react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import {ManagedAssetDoughnut} from "./ManagedAssetStatic";

const BAR_ACTIVE = "#1a56db";
const BAR_DEFAULT = "#b8d9f0";
const AXIS_COLOR = "#666666";
const GRID_COLOR = "#e0e0e0";

function normalizeChartItems(data) {
    if (!Array.isArray(data)) return [];

    return data.map((item, index) => {
        if (item.cnt != null && !item.cols) {
            return {...item, cnt: Number(item.cnt) || 0};
        }

        if (item.cols) {
            let sum = 0;
            Object.keys(item.cols).forEach((key) => {
                if (key.startsWith("value_")) {
                    sum += Number(item.cols[key]) || 0;
                }
            });
            return {...item, cnt: sum};
        }

        return {...item, id: item.id ?? index, cnt: Number(item.cnt) || 0};
    });
}

const truncateLabel = (name, max = 3) => {
    if (!name) return "";
    return name.length > max ? `${name.slice(0, max)}...` : name;
};

function BarTooltip({active, payload}) {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload;
    if (!row) return null;
    return (
        <div className="rounded-[4px] border border-[rgba(0,0,0,0.08)] bg-white px-3 py-2 text-[12px] shadow-sm">
            <div className="font-medium text-[#333]">{row.fullName}</div>
            <div className="text-[#666]">건수 : {row.cnt?.toLocaleString()}</div>
        </div>
    );
}

function renderActiveBarLabel(highlightIndex) {
    return (props) => {
        const {x, y, width, value, index} = props;
        if (index !== highlightIndex || value == null) return null;
        return (
            <text
                x={x + width / 2}
                y={y - 8}
                fill={BAR_ACTIVE}
                textAnchor="middle"
                fontSize={13}
                fontWeight={700}
            >
                {value}
            </text>
        );
    };
}

function AssetTypeStatusBar({items, highlightIndex, onHighlightChange}) {
    const chartRows = useMemo(
        () =>
            items.map((item, index) => ({
                ...item,
                index,
                fullName: item.name,
                label: truncateLabel(item.name),
            })),
        [items]
    );

    const maxCnt = useMemo(
        () => Math.max(...items.map((item) => Number(item.cnt) || 0), 0),
        [items]
    );

    const yMax = useMemo(() => {
        if (maxCnt <= 0) return 10;
        if (maxCnt <= 50) return Math.ceil(maxCnt / 10) * 10 || 10;
        const step = Math.ceil(maxCnt / 5 / 10) * 10;
        return Math.ceil(maxCnt / step) * step;
    }, [maxCnt]);

    const yTicks = useMemo(() => {
        const step = yMax <= 50 ? 10 : Math.round(yMax / 5);
        const ticks = [];
        for (let value = 0; value <= yMax; value += step) {
            ticks.push(value);
        }
        return ticks.length > 1 ? ticks : [0, yMax];
    }, [yMax]);

    const activeIndex =
        highlightIndex ??
        chartRows.findIndex((row) => Number(row.cnt) === maxCnt && maxCnt > 0);

    return (
        <div className="h-full w-full min-h-0">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    data={chartRows}
                    margin={{top: 20, right: 12, left: 0, bottom: 4}}
                    barCategoryGap="22%"
                >
                    <CartesianGrid strokeDasharray="4 4" stroke={GRID_COLOR} vertical={false}/>
                    <XAxis
                        dataKey="label"
                        interval={0}
                        height={36}
                        tickMargin={6}
                        tick={{fill: AXIS_COLOR, fontSize: 11}}
                        axisLine={{stroke: AXIS_COLOR, strokeWidth: 1}}
                        tickLine={false}
                    />
                    <YAxis
                        domain={[0, yMax]}
                        ticks={yTicks}
                        width={32}
                        tick={{fill: AXIS_COLOR, fontSize: 11}}
                        axisLine={{stroke: AXIS_COLOR, strokeWidth: 1}}
                        tickLine={false}
                    />
                    <Tooltip content={<BarTooltip/>} cursor={{fill: "rgba(26, 86, 219, 0.06)"}}/>
                    <Bar
                        dataKey="cnt"
                        radius={[4, 4, 0, 0]}
                        maxBarSize={52}
                        label={renderActiveBarLabel(activeIndex >= 0 ? activeIndex : undefined)}
                        onMouseEnter={(_, index) => onHighlightChange?.(index)}
                        onMouseLeave={() => onHighlightChange?.(undefined)}
                    >
                        {chartRows.map((row, index) => (
                            <Cell
                                key={`${row.id ?? row.name}-${index}`}
                                fill={index === activeIndex ? BAR_ACTIVE : BAR_DEFAULT}
                            />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

/**
 * 자산 그룹/업무/부서별 현황 — 좌측 막대 + 우측 도넛 (통합대시보드 스타일)
 */
function AssetTypeStatusChart({data}) {
    const items = useMemo(() => normalizeChartItems(data), [data]);
    const total = useMemo(
        () => items.reduce((sum, item) => sum + (Number(item.cnt) || 0), 0),
        [items]
    );
    const [highlightIndex, setHighlightIndex] = useState(undefined);
    const hasData = items.some((item) => Number(item.cnt) > 0);

    useEffect(() => {
        setHighlightIndex(undefined);
    }, [data]);

    if (!hasData) {
        return (
            <div className="flex h-full min-h-[180px] items-center justify-center bg-[#fafafa] text-[13px] text-[#999]">
                No Data.
            </div>
        );
    }

    return (
        <div className="asset-type-status-chart flex h-full min-h-0 w-full flex-col bg-white md:flex-row [&_*:focus]:outline-none [&_.recharts-active-dot]:outline-none">
            <div className="mobile-hidden h-[220px] min-h-[220px] min-w-0 shrink-0 md:h-full md:flex-[3] md:pr-2">
                <AssetTypeStatusBar
                    items={items}
                    highlightIndex={highlightIndex}
                    onHighlightChange={setHighlightIndex}
                />
            </div>
            <div className="mobile-full h-[220px] min-h-[220px] min-w-0 md:h-full md:flex-[2] md:border-l md:border-[#eef2f7] md:pl-2">
                <ManagedAssetDoughnut items={items} total={total}/>
            </div>
        </div>
    );
}

export default AssetTypeStatusChart;
